import { join, relative, resolve, isAbsolute } from "node:path";
import { z } from "zod";
import { ParseError } from "../core/errors.js";
import { unsafeBrandResumeToken, type ResumeToken, type Url } from "../data-model/primitives.js";
import { sha1, userCacheDir } from "./_fs.js";

const TokenPayloadSchema = z.object({
  v: z.literal(1),
  stateFile: z.string().min(1),
  urlHash: z.string().min(1),
});

type TokenPayload = z.infer<typeof TokenPayloadSchema>;

export function encodeResumeToken(stateFile: string, url: Url): ResumeToken {
  const payload: TokenPayload = { v: 1, stateFile, urlHash: sha1(url) };
  return unsafeBrandResumeToken(Buffer.from(JSON.stringify(payload)).toString("base64url"));
}

export function decodeResumeToken(token: ResumeToken): {
  readonly stateFile: string;
  readonly urlHash: string;
} {
  let raw: unknown;
  try {
    const text = Buffer.from(token, "base64url").toString("utf8");
    raw = JSON.parse(text);
  } catch (cause) {
    throw new ParseError("Invalid resume token: not base64-encoded JSON", { cause });
  }
  const parsed = TokenPayloadSchema.safeParse(raw);
  if (!parsed.success) {
    throw new ParseError("Invalid resume token: unsupported payload", { cause: parsed.error });
  }
  return { stateFile: parsed.data.stateFile, urlHash: parsed.data.urlHash };
}

function assertWithinCacheDir(stateFile: string): void {
  const root = resolve(join(userCacheDir(), "webnovel-downloader"));
  const rel = relative(root, resolve(stateFile));
  // Reject the root itself (rel === "") as well as any escape: stateFile must be
  // a file *inside* the cache dir, never the directory.
  if (rel === "" || rel.startsWith("..") || isAbsolute(rel)) {
    throw new ParseError("Resume token state path escapes the cache directory");
  }
}

export function verifyResumeToken(token: ResumeToken, url: Url): { readonly stateFile: string } {
  const { stateFile, urlHash } = decodeResumeToken(token);
  if (urlHash !== sha1(url)) {
    throw new ParseError("Resume token does not match URL");
  }
  // Containment is enforced on the token branch only: a token's stateFile is
  // attacker-influenceable, but an explicit `{ stateFile }` resume option is
  // caller-controlled and intentionally not checked here.
  assertWithinCacheDir(stateFile);
  return { stateFile };
}
